import { getOverviewYield, getShareClassName } from "@/lib";
import { hooks } from "@/styles/breakpoints";
import type { ShareClass } from "@/types";
import parse from "html-react-parser";
import * as styles from "./KeyInfo.css";
import type { KeyInfoTables } from "./types";

const { useIsBelow } = hooks;

export default function KeyInfo({
	tables,
	yields,
	sectors,
	placement,
	primaryShareClass,
	asAtDate,
}: {
	tables: KeyInfoTables[];
	yields: KeyInfoTables;
	sectors: KeyInfoTables;
	primaryShareClass?: ShareClass;
	asAtDate?: string;
} & styles.Variants) {
	const isBelowL = useIsBelow("l");
	const kiTables = tables.flatMap((t) => t.data?.map((d) => d));
	const kiSectors = sectors.data;
	const kiYield = getOverviewYield(yields.data);
	const shareClassName = primaryShareClass
		? getShareClassName(primaryShareClass)
		: "";

	const [yieldKey, yieldValue] = kiYield;
	const yieldData = (
		yieldValue
			? [
					shareClassName ? `${yieldKey} <br> (${shareClassName})` : yieldKey,
					yieldValue,
				]
			: ["", ""]
	) as [string, string];

	const uniqueData = (data: ([] | [string, string | null])[]) => {
		const uniqueLabels = new Set();
		return data.filter(([label]) => {
			if (label && !uniqueLabels.has(label)) {
				uniqueLabels.add(label);
				return true;
			}
			return false;
		});
	};
	const nonBlankData = (d: [string, string | null] | []) =>
		!!d && d.length === 2 && d[1] && d[1].length > 0 && d[1] !== "-";
	const nonSectorData = uniqueData(
		[...kiTables, yieldData].filter((d) => !!d).filter(nonBlankData),
	);
	const sectorsData = kiSectors && uniqueData(kiSectors.filter(nonBlankData));

	return (
		<div className={styles.wrap({ placement })}>
			{nonSectorData.length > 0 && (
				<div className={styles.nonSectorsWrap({ placement })}>
					<table>
						<tbody>
							{nonSectorData.map(([label, value]) => (
								<tr key={label}>
									<th className={styles.th({ placement, type: "nonSector" })}>
										{parse(label as string)}
									</th>
									<td className={styles.td({ placement, type: "nonSector" })}>
										{value}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{isBelowL && asAtDate && (
				<div className={styles.asAtDate}>{asAtDate}</div>
			)}
			{sectorsData && sectorsData.length > 0 && (
				<div className={styles.sectorsWrap({ placement })}>
					<table>
						{sectorsData.map(([label, value]) => (
							<tbody key={label}>
								<tr key={label}>
									<th className={styles.th({ placement, type: "sector" })}>
										{label}
									</th>
								</tr>
								<tr>
									<td className={styles.td({ placement, type: "sector" })}>
										{value}
									</td>
								</tr>
							</tbody>
						))}
					</table>
				</div>
			)}
		</div>
	);
}
